import React, { useState } from 'react';
import styled from 'styled-components';
import Rating from 'react-rating-stars-component';

const ReviewContainer = styled.div` 
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin: 20px auto;
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;
  position: relative;
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ReviewerInfo = styled.div`
  display: flex;
  align-items: center;
`;

const ReviewerName = styled.h2`
  font-size: 1.5em;
  margin-bottom: 10px;
  margin-left: 10px;
  color: #000;
`;

const ProfileImage = styled.img`
  border-radius: 50%;
  width: 50px;
  height: 50px;
  object-fit: cover;
  margin-bottom: 10px;
`;

const BusinessName = styled.h3`
  font-size: 1.2em;
  color: #888;
`;

const ReviewText = styled.p`
  margin: 10px 0;
  line-height: 1.5;
`;

const Address = styled.div`
  font-size: 0.9em;
  color: #666;
`;

const StarRating = styled.div`
  margin: 10px 0;
`;

const ThreeDotsMenu = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
`;

const MenuList = styled.ul`
  position: absolute;
  top: 30px;
  right: 0;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  padding: 0;
  margin: 0;
  list-style-type: none;
  z-index: 100;
  display: ${(props) => (props.open ? 'block' : 'none')};
`;

const MenuItem = styled.li`
  padding: 10px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const EditForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  color: #000000ff;
  border-radius: 5px;
  border: 1px solid #ccc;
  resize: none;
`;

const SaveButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background: #218838;
  }
`;

const ReviewReply = styled.div`
  background-color: #eef6ff;
  border-radius: 8px;
  padding: 10px;
  margin-top: 20px;
`;

const ReviewReplyHeader = styled.h3`
  margin-top: 0;
`;

const Review = ({ review, onReportInappropriate }) => { //review: Data object containing the review info. onReportInappropriate: Function to call when user clicks "Report".
  // Defensive defaults These lines protect the code from crashing if any value is missing in review.
  const reviewer = review.reviewer || {};
  const profilePhotoUrl = reviewer.profilePhotoUrl || '';
  const displayName = reviewer.displayName || 'Anonymous';
  const businessName = review.businessName || '';
  const address = review.address || {};
  const addressLines = address.addressLines || [];
  const locality = address.locality || '';
  const administrativeArea = address.administrativeArea || '';
  const postalCode = address.postalCode || '';
  const regionCode = address.regionCode || '';
  const comment = review.comment || '';
  const starRating = review.starRating || 'ONE';
  const reviewReply = review.reviewReply || null;
  //State hooks
  const [showMenu, setShowMenu] = useState(false); //showMenu: Controls the visibility of the ⋮ menu.
  const [isEditingReply, setIsEditingReply] = useState(false); //isEditingReply: Controls if the reply is being edited.
  const [editedReply, setEditedReply] = useState(reviewReply ? reviewReply.comment : ''); //editedReply: Stores the new reply text.
  const [replyDeleted, setReplyDeleted] = useState(false); //replyDeleted: Flags if the reply was deleted (so it’s hidden).

  const handleMenuToggle = () => { //Toggles the 3-dot menu open/close.
    setShowMenu(!showMenu);
  };

  const handleEditReplyClick = () => {
    setIsEditingReply(true);
    setShowMenu(false);
  };

  const handleSaveReplyClick = () => { //Saves the reply text (currently only in local UI — not sent to backend).
    review.reviewReply.comment = editedReply;
    setIsEditingReply(false);
  };

  const handleReportClick = () => {
    onReportInappropriate(review);
    setShowMenu(false);
  };

  const handleDeleteReplyClick = () => { //Marks the reply as deleted (hides it visually).
    setReplyDeleted(true);
    setShowMenu(false);
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-12">
          <ReviewContainer className="shadow bg-white rounded">
            <ReviewHeader>
              <ReviewerInfo>
                <ProfileImage src={profilePhotoUrl} alt="Reviewer" />
                <div>
                  <h2 style={{ color: 'black' }}>{displayName}</h2>
                  <BusinessName>{businessName}</BusinessName>
                  <Address>
                    {addressLines.join(', ')}<br />
                    {locality}, {administrativeArea} {postalCode}, {regionCode}
                  </Address>
                </div>
              </ReviewerInfo>
              <ThreeDotsMenu onClick={handleMenuToggle}>⋮</ThreeDotsMenu>
              <MenuList open={showMenu}>
                <MenuItem onClick={handleEditReplyClick}>Edit Reply</MenuItem>
                <MenuItem onClick={handleDeleteReplyClick}>Delete Reply</MenuItem>
                <MenuItem onClick={handleReportClick}>Report Inappropriate</MenuItem>
              </MenuList>
            </ReviewHeader>

            <ReviewText>{comment}</ReviewText>

            <StarRating>
              <Rating
                count={5}
                value={
                  starRating === 'FIVE' ? 5 :
                    starRating === 'FOUR' ? 4 :
                      starRating === 'THREE' ? 3 :
                        starRating === 'TWO' ? 2 : 1
                }
                edit={false}
                size={24}
                activeColor="#ffd700"
              />
            </StarRating>

            {reviewReply && !replyDeleted && (
              <ReviewReply>
                <ReviewReplyHeader>Reply:</ReviewReplyHeader>
                {isEditingReply ? (
                  <EditForm>
                    <TextArea
                      value={editedReply}
                      onChange={(e) => setEditedReply(e.target.value)}
                    />
                    <SaveButton onClick={handleSaveReplyClick}>Save</SaveButton>
                  </EditForm>
                ) : (
                  <ReviewText>{reviewReply.comment}</ReviewText>
                )}
              </ReviewReply>
            )}
          </ReviewContainer>
        </div>
      </div>
    </div>
  );
};

export default Review;
